import { Helmet } from "react-helmet-async";
import { useState, useEffect, useRef, useCallback } from "react";
import Layout from "../../Layout";
import { Card, Input, Button, Select, Tag, Empty, message } from "antd";
import {
    ApiOutlined,
    DisconnectOutlined,
    DeleteOutlined,
    ThunderboltOutlined,
} from "@ant-design/icons";
import { createClient, SupabaseClient, RealtimeChannel } from "@supabase/supabase-js";

/**
 * Trang thử nghiệm Supabase Realtime (chưa dùng chính thức).
 * Cho phép nhập cấu hình ngay trên giao diện để test nhanh nhiều kịch bản
 * (bảng, loại event, filter) mà không cần rebuild. Khi nghiệm thu xong sẽ
 * tích hợp vào RoomChat theo hướng: nhận event -> refetch API hiện có.
 */

interface ReceivedEvent {
    key: number;
    receivedAt: string;
    eventType: string;
    table: string;
    payload: any;
}

interface DemoConfig {
    url: string;
    anonKey: string;
    table: string;
    event: string;
    filter: string;
}

const STORAGE_KEY = "realtime_demo_config";

const DEFAULT_CONFIG: DemoConfig = {
    url: process.env.REACT_APP_SUPABASE_URL || "https://api.maximus-nhon.online",
    anonKey: process.env.REACT_APP_SUPABASE_ANON_KEY || "",
    table: "channel_messages",
    event: "INSERT",
    filter: "",
};

const loadConfig = (): DemoConfig => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) return { ...DEFAULT_CONFIG, ...JSON.parse(raw) };
    } catch { /* dùng mặc định */ }
    return DEFAULT_CONFIG;
};

const STATUS_DISPLAY: Record<string, { label: string; color: string }> = {
    idle: { label: "Chưa kết nối", color: "default" },
    connecting: { label: "Đang kết nối...", color: "processing" },
    SUBSCRIBED: { label: "Đang lắng nghe", color: "success" },
    CLOSED: { label: "Đã ngắt kết nối", color: "default" },
    CHANNEL_ERROR: { label: "Lỗi kênh", color: "error" },
    TIMED_OUT: { label: "Hết thời gian chờ", color: "warning" },
};

export default function RealtimeDemo() {
    const [config, setConfig] = useState<DemoConfig>(loadConfig);
    const [status, setStatus] = useState("idle");
    const [events, setEvents] = useState<ReceivedEvent[]>([]);
    const clientRef = useRef<SupabaseClient | null>(null);
    const channelRef = useRef<RealtimeChannel | null>(null);
    const eventKeyRef = useRef(0);

    const isListening = status === "SUBSCRIBED" || status === "connecting";

    const disconnect = useCallback(() => {
        if (channelRef.current && clientRef.current) {
            clientRef.current.removeChannel(channelRef.current);
        }
        channelRef.current = null;
        clientRef.current = null;
        setStatus("idle");
    }, []);

    // Cleanup: đóng websocket khi rời trang để tránh rò rỉ kết nối
    useEffect(() => disconnect, [disconnect]);

    const connect = () => {
        if (!config.url.trim() || !config.anonKey.trim()) {
            message.warning("Vui lòng nhập Supabase URL và Anon Key");
            return;
        }
        if (!config.table.trim()) {
            message.warning("Vui lòng nhập tên bảng");
            return;
        }

        localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
        disconnect();
        setStatus("connecting");

        try {
            const client = createClient(config.url.trim(), config.anonKey.trim());
            clientRef.current = client;

            const channel = client
                .channel(`realtime-demo:${config.table.trim()}`)
                .on(
                    "postgres_changes" as any,
                    {
                        event: config.event as any,
                        schema: "public",
                        table: config.table.trim(),
                        ...(config.filter.trim() ? { filter: config.filter.trim() } : {}),
                    },
                    (payload: any) => {
                        eventKeyRef.current += 1;
                        setEvents((prev) => [
                            {
                                key: eventKeyRef.current,
                                receivedAt: new Date().toLocaleTimeString("vi-VN"),
                                eventType: payload.eventType || config.event,
                                table: payload.table || config.table,
                                payload: payload.new ?? payload.old ?? payload,
                            },
                            ...prev,
                        ].slice(0, 100)); // giữ tối đa 100 event để tránh nặng trang
                    }
                )
                .subscribe((subStatus) => {
                    setStatus(subStatus);
                    if (subStatus === "CHANNEL_ERROR") {
                        message.error("Kết nối kênh thất bại — kiểm tra URL/Anon Key/Realtime đã bật cho bảng chưa");
                    }
                });

            channelRef.current = channel;
        } catch (error) {
            console.error("Failed to connect Supabase Realtime:", error);
            setStatus("CHANNEL_ERROR");
            message.error("Không khởi tạo được Supabase client — kiểm tra lại URL");
        }
    };

    const statusDisplay = STATUS_DISPLAY[status] || { label: status, color: "default" };

    return (
        <Layout>
            <Helmet>
                <title>Realtime Demo | Khothi</title>
            </Helmet>

            <div className="max-w-[1100px] mx-auto">
                {/* CẤU HÌNH */}
                <Card
                    title={
                        <div className="flex items-center gap-2">
                            <ThunderboltOutlined className="text-[#3e91ab]" />
                            <span>Thử nghiệm Supabase Realtime</span>
                            <Tag color={statusDisplay.color}>{statusDisplay.label}</Tag>
                        </div>
                    }
                    className="mb-4 dark:bg-[#141414] dark:border-[#303030]"
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <div className="text-[13px] text-gray-500 mb-1">Supabase URL</div>
                            <Input
                                value={config.url}
                                onChange={(e) => setConfig({ ...config, url: e.target.value })}
                                placeholder="https://api.maximus-nhon.online"
                                disabled={isListening}
                            />
                        </div>
                        <div>
                            <div className="text-[13px] text-gray-500 mb-1">Anon Key</div>
                            <Input.Password
                                value={config.anonKey}
                                onChange={(e) => setConfig({ ...config, anonKey: e.target.value })}
                                placeholder="eyJhbGciOi..."
                                disabled={isListening}
                            />
                        </div>
                        <div>
                            <div className="text-[13px] text-gray-500 mb-1">Bảng (schema public)</div>
                            <Input
                                value={config.table}
                                onChange={(e) => setConfig({ ...config, table: e.target.value })}
                                placeholder="channel_messages"
                                disabled={isListening}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <div className="text-[13px] text-gray-500 mb-1">Sự kiện</div>
                                <Select
                                    className="w-full"
                                    value={config.event}
                                    onChange={(v) => setConfig({ ...config, event: v })}
                                    disabled={isListening}
                                    options={[
                                        { value: "INSERT", label: "INSERT" },
                                        { value: "UPDATE", label: "UPDATE" },
                                        { value: "DELETE", label: "DELETE" },
                                        { value: "*", label: "* (tất cả)" },
                                    ]}
                                />
                            </div>
                            <div>
                                <div className="text-[13px] text-gray-500 mb-1">
                                    Filter (tùy chọn)
                                </div>
                                <Input
                                    value={config.filter}
                                    onChange={(e) => setConfig({ ...config, filter: e.target.value })}
                                    placeholder="channel_connection_id=eq.1"
                                    disabled={isListening}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex gap-2 mt-4">
                        {!isListening ? (
                            <Button
                                type="primary"
                                icon={<ApiOutlined />}
                                onClick={connect}
                                className="bg-[#3e91ab] border-[#3e91ab] hover:!bg-[#357f96] hover:!border-[#357f96]"
                            >
                                Kết nối
                            </Button>
                        ) : (
                            <Button danger icon={<DisconnectOutlined />} onClick={disconnect}>
                                Ngắt kết nối
                            </Button>
                        )}
                        <Button
                            icon={<DeleteOutlined />}
                            onClick={() => setEvents([])}
                            disabled={events.length === 0}
                        >
                            Xóa log
                        </Button>
                    </div>

                    <div className="text-[12px] text-gray-400 mt-3">
                        Gợi ý filter: <code>channel_connection_id=eq.1</code> (một kênh),{" "}
                        <code>channel_connection_id=in.(1,2,3)</code> (nhiều kênh — dùng cho nhóm gộp trang),{" "}
                        <code>channel_customer_id=eq.41</code> (một khách). Để trống = nhận toàn bảng.
                    </div>
                </Card>

                {/* LOG SỰ KIỆN */}
                <Card
                    title={`Sự kiện nhận được (${events.length})`}
                    className="dark:bg-[#141414] dark:border-[#303030]"
                >
                    {events.length === 0 ? (
                        <Empty
                            description={
                                status === "SUBSCRIBED"
                                    ? "Đang đợi sự kiện mới từ DB... (thử nhắn tin vào một kênh đã kết nối)"
                                    : "Chưa có sự kiện. Nhập cấu hình và bấm Kết nối."
                            }
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                    ) : (
                        <div className="flex flex-col gap-2 max-h-[60vh] overflow-y-auto">
                            {events.map((ev) => (
                                <div
                                    key={ev.key}
                                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-3"
                                >
                                    <div className="flex items-center gap-2 mb-2 text-[12px]">
                                        <Tag
                                            color={
                                                ev.eventType === "INSERT"
                                                    ? "green"
                                                    : ev.eventType === "UPDATE"
                                                        ? "blue"
                                                        : "red"
                                            }
                                        >
                                            {ev.eventType}
                                        </Tag>
                                        <span className="text-gray-500">{ev.table}</span>
                                        <span className="text-gray-400 ml-auto">{ev.receivedAt}</span>
                                    </div>
                                    <pre className="text-[12px] bg-gray-50 dark:bg-[#1f2937] rounded p-2 overflow-x-auto whitespace-pre-wrap break-all m-0">
                                        {JSON.stringify(ev.payload, null, 2)}
                                    </pre>
                                </div>
                            ))}
                        </div>
                    )}
                </Card>
            </div>
        </Layout>
    );
}
